import { useState, useMemo, useRef } from 'react';
import { Estimate, EstimateItem, EstimateAdjustments, PricingSnapshot, ProposalSettings, SiteConditions, EstimateZone } from '../types';
import { useSettings } from '../contexts/SettingsContext';
import { isPassThroughType } from '../utils/passThroughPricing';
import { priceLineByType, repriceFromPin, applyFactor, type LinePricingDials } from '../utils/lineItemPricing';
import { computeEstimateTotals, laborCostPerHourFromSnapshot, type EstimateMathInput } from '../services/estimateMath';
import { solveScaleForNetMargin, scaleItemsByFactor, baseRateOf, type MarginSolveResult } from '../services/marginSolver';
import { canEditPricing } from '../utils/estimateEditability';

/**
 * hooks/useEstimateForm.ts — El estado del constructor de presupuestos, y el
 * único sitio donde una partida cambia de precio mientras se edita.
 *
 * LA INVARIANTE (PLAN-pricing-model-v2, Parte A.1). Toda fila que no sea pase
 * directo cumple, después de CUALQUIER mutación:
 *
 *     baseRate = el precio que produce la REGLA de la fila (pin o política)
 *     rate     = baseRate × k          (k = netMarginFactor del estimado)
 *     total    = rate × cantidad
 *
 * Lo que se guarda es `baseRate`; `rate` es derivado. Antes no existía ese
 * eslabón: cada rama de `updateItem` derivaba un precio del costo por su cuenta
 * —con fórmulas que ni siquiera coincidían entre sí— y el deslizador reescribía
 * `rate` encima de sí mismo. De ahí salían los dos fallos medidos: teclear 30 en
 * un material devolvía 42,9%, y el precio dependía del CAMINO (clic al 10% →
 * 13,46% de margen; arrastrar al 10% → 5,71%).
 *
 * MARKUP = SOBRE EL COSTO. MARGEN = SOBRE EL PRECIO. No son el mismo dinero:
 * sobre $100 de costo, un 30% de markup deja $30 y un 30% de margen deja $42,86.
 * Por eso el metro (`pinnedMetric`) viaja con el número que el contratista
 * teclea, y por eso Material se cotiza por markup y Labor / Equipment /
 * Subcontractor por margen. Un Fee va a costo pelado y no admite ni pin ni
 * porcentaje.
 */

interface UseEstimateFormProps {
    initialEstimate: Estimate;
}

/**
 * El snapshot de precios, más lo que el modelo v2 necesita recordar del dial.
 *
 * POR QUÉ VIVEN AQUÍ Y NO EN `estimates`. `netMarginTarget` NO se persiste hoy:
 * no hay columna `net_margin_target` en la tabla y `estimateAllowedKeys`
 * (services/database.ts ~245-270) no la lista, así que `filterEstimate` la tira
 * al guardar y el objetivo se perdía en cada recarga sin que nadie se enterara.
 * `pricingSnapshot` en cambio es `jsonb`, ya está en la lista blanca y ya viaja
 * entero — así que el objetivo y el factor se guardan DENTRO de él. No hace
 * falta migración.
 *
 * `netMarginFactor` se persiste junto al objetivo, y no es opcional que lo haga:
 * las filas guardan `baseRate` y `rate` ya escalado. Si al recargar el factor
 * volviera a 1, la primera edición de cualquier fila la re-cotizaría a su precio
 * base mientras las demás siguen escaladas, y el documento quedaría a dos
 * niveles de precio distintos sin un aviso.
 */
export interface PricingSnapshotV2 extends PricingSnapshot {
    /** Margen objetivo SOBRE EL TRABAJO PROPIO, en puntos porcentuales. */
    netMarginTarget?: number | null;
    /** El factor `k` que se aplicó para conseguirlo. 1 = «exactamente mis diales». */
    netMarginFactor?: number;
}

/**
 * Lo que se puede cambiar de la POLÍTICA de precios de un estimado.
 *
 * DOS COSAS DISTINTAS VIVEN EN EL MISMO `pricingSnapshot`, y confundirlas es el
 * fallo que la ola 4 corrige (decisión 2 del plan):
 *
 *   · EL COSTO CONGELADO — `breakEvenRate`, `baseLaborCost`, `laborBurdenPercent`,
 *     `overheadPerManHour`, `targetHourlyRate`. NO se toca. Es la foto de lo que
 *     al taller le costaba producir el trabajo el día que se cotizó, y es lo
 *     único que hace que un estimado aceptado hace tres meses siga cuadrando
 *     cuando los sueldos ya subieron. Solo lo reemplaza
 *     `refreshSnapshotFromCurrentSettings`, que es un acto explícito.
 *
 *   · LA POLÍTICA DE PRECIO — `targetMarginPercent` y `materialMarkupPercent`.
 *     Sí es editable, y es lo que este objeto describe. Nace copiada de los
 *     diales del taller (Ajustes ▸ Precios) y a partir de ahí pertenece a ESTE
 *     documento: cambiarla aquí no toca los ajustes de los compañeros ni los de
 *     los estimados futuros, que es justo lo que hacía el panel «Precio rápido»
 *     —vivía en la cabecera de un estimado y escribía `settings.pricing.*`—.
 */
export interface PricingPolicyPatch {
    /** Margen objetivo SOBRE EL PRECIO, en puntos porcentuales. */
    targetMarginPercent?: number;
    /** Markup de material SOBRE EL COSTO, en puntos porcentuales. */
    materialMarkupPercent?: number;
}

export const useEstimateForm = ({ initialEstimate }: UseEstimateFormProps) => {
    const { settings, metrics } = useSettings();

    // --- STATE ---
    const [items, setItems] = useState<EstimateItem[]>(initialEstimate.items || []);
    const [targetMarginGoal, setTargetMarginGoal] = useState<number | null>(null);
    const [adjustments, setAdjustments] = useState<EstimateAdjustments>({
        tripCharge: initialEstimate.adjustments?.tripCharge || false,
        emergencySurcharge: initialEstimate.adjustments?.emergencySurcharge || false,
        applyTax: initialEstimate.adjustments?.applyTax || false,
        taxLabor: initialEstimate.adjustments?.taxLabor ?? (settings.pricing.taxLaborDefault ?? false),
        /* `??`, no `||`: una tasa guardada en 0 es FALSY, así que un cliente
         * exento de impuesto —que se guardó correctamente en 0 %— volvía a
         * abrirse en 8,25 % y se le cobraba impuesto sin que nadie lo tocara. */
        taxRate: initialEstimate.adjustments?.taxRate ?? 8.25,
        minJobFeeApplied: initialEstimate.adjustments?.minJobFeeApplied || false,
        discount: initialEstimate.adjustments?.discount || 0,
        discountType: initialEstimate.adjustments?.discountType || 'flat'
    });
    const [proposalSettings, setProposalSettings] = useState<ProposalSettings>(initialEstimate.proposalSettings || {
        title: `Estimate for ${initialEstimate.clientName}`,
        introMessage: 'Please find our detailed estimate below.',
        coverImageTheme: 'Modern',
        showLineItemPricing: true,
        showTotals: true
    });
    const [siteConditions, setSiteConditions] = useState<SiteConditions>(initialEstimate.siteConditions || {
        accessWidthInches: 48,
        soilType: 'Sand',
        slopePercent: 0,
        difficultyFactor: 1.0,
        distanceToStreetFt: 20
    });
    const [zones, setZones] = useState<EstimateZone[]>(initialEstimate.zones || []);

    // Use existing snapshot or current metrics if new
    const [snapshot, setSnapshot] = useState<PricingSnapshotV2>(initialEstimate.pricingSnapshot || {
        overheadPerManHour: metrics.overheadPerManHour,
        laborBurdenPercent: metrics.avgLaborBurdenPercent,
        targetMarginPercent: settings.pricing.targetMarginPercent,
        baseLaborCost: metrics.avgHourlyWage,
        targetHourlyRate: metrics.targetHourlyRate,
        materialMarkupPercent: settings.pricing.defaultMaterialMarkupPercent,
        breakEvenRate: metrics.breakEvenRate,
        capturedAt: new Date().toISOString(),
        settingsSchemaVersion: 2,
        breakdownSummary: {
            directLaborCost: metrics.breakdown?.totalDirectLaborCost ?? 0,
            totalOverhead: metrics.breakdown?.totalOverhead ?? 0,
            billableHours: metrics.breakdown?.totalBillableHours ?? 0,
        },
    });

    /* El dial de margen neto (Parte A.4), en dos números.
     *
     * `netMarginFactor` (k) es el multiplicador que se aplica al precio base de
     * todas las partidas que no son pase directo. k = 1 es el ancla honesta:
     * «exactamente mis diales». Se siembra desde el snapshot guardado para que
     * al reabrir un presupuesto la invariante siga cumpliéndose con los precios
     * que hay en disco.
     *
     * `netMarginTarget` es lo que el contratista PIDIÓ. Se guarda aparte de lo
     * conseguido porque añadir una partida después de resolver desplaza el
     * margen real, y la interfaz tiene que poder decir «objetivo 35% · real
     * 33,2%» en vez de mentir con uno de los dos (decisión 6). */
    const [netMarginFactor, setNetMarginFactor] = useState<number>(() => {
        const guardado = (initialEstimate.pricingSnapshot as PricingSnapshotV2 | undefined)?.netMarginFactor;
        return Number.isFinite(guardado as number) && (guardado as number) > 0 ? (guardado as number) : 1;
    });
    const [netMarginTarget, setNetMarginTargetState] = useState<number | null>(() => {
        const enSnapshot = (initialEstimate.pricingSnapshot as PricingSnapshotV2 | undefined)?.netMarginTarget;
        if (Number.isFinite(enSnapshot as number)) return enSnapshot as number;
        /* `Estimate.netMarginTarget` existe en el tipo pero NO sobrevive al
         * guardado (no está en la lista blanca). Se lee igual, por si alguien lo
         * trae en memoria, pero lo que persiste es la copia del snapshot. */
        return Number.isFinite(initialEstimate.netMarginTarget as number)
            ? (initialEstimate.netMarginTarget as number)
            : null;
    });
    /** El último intento de resolver, para que la interfaz explique un fracaso. */
    const [netMarginSolve, setNetMarginSolve] = useState<MarginSolveResult | null>(null);

    /* ¿Este estimado sigue siendo un borrador?
     *
     * Un Sent / Accepted / Paid es un documento que un cliente YA VIO, así que
     * sus precios quedan como los vio (decisión 7). La regla NO se escribe aquí:
     * viene de utils/estimateEditability, el mismo sitio del que la lee el panel
     * del snapshot (`canRefresh`). Una segunda definición de «cerrado» es una
     * segunda respuesta a «¿puedo tocar este precio?». */
    const preciosEditables = canEditPricing(initialEstimate.status);

    /** Replaces the frozen snapshot with one captured from current settings.
     *  Only safe to expose for Draft / Review estimates — never after Sent/Accepted. */
    const refreshSnapshotFromCurrentSettings = () => {
        setSnapshot(prev => ({
            overheadPerManHour: metrics.overheadPerManHour,
            laborBurdenPercent: metrics.avgLaborBurdenPercent,
            targetMarginPercent: settings.pricing.targetMarginPercent,
            baseLaborCost: metrics.avgHourlyWage,
            targetHourlyRate: metrics.targetHourlyRate,
            materialMarkupPercent: settings.pricing.defaultMaterialMarkupPercent,
            breakEvenRate: metrics.breakEvenRate,
            capturedAt: new Date().toISOString(),
            settingsSchemaVersion: 2,
            breakdownSummary: {
                directLaborCost: metrics.breakdown?.totalDirectLaborCost ?? 0,
                totalOverhead: metrics.breakdown?.totalOverhead ?? 0,
                billableHours: metrics.breakdown?.totalBillableHours ?? 0,
            },
            /* OJO: esto RE-SIEMBRA también la política del estimado
             * (`targetMarginPercent` / `materialMarkupPercent`) desde los diales
             * del taller. Es lo que promete el botón —«actualizar desde la
             * configuración actual»— y por eso es un acto explícito y no algo
             * que ocurra al guardar un campo: volver a nacer con los diales de
             * hoy incluye volver a nacer con su política.
             *
             * El dial de margen neto NO se recaptura: refrescar el snapshot es
             * traer los costos del taller de hoy, no olvidar lo que el
             * contratista pidió para ESTE presupuesto. Si se
             * perdieran aquí, los precios de las filas seguirían escalados por un
             * k que ya nadie recuerda y la invariante quedaría rota en silencio. */
            netMarginTarget: prev.netMarginTarget,
            netMarginFactor: prev.netMarginFactor,
        }));
    };

    /* ── LA POLÍTICA DE PRECIOS ES DEL ESTIMADO ──────────────────────────────
     *
     * De aquí salen los dos porcentajes con los que se cotiza cada fila, y por
     * aquí pasan `priceLineByType`, `repriceFromPin`, `applyMargin` y
     * `applyMaterialMarkup`. Un solo sitio: es lo que impide que dos ramas
     * cotizen con diales distintos, que es cómo dos filas de mano de obra
     * idénticas acababan con $9,24/h de diferencia en el mismo documento.
     *
     * SE LEE DEL SNAPSHOT, NO DE LOS AJUSTES VIVOS (decisión 2 del plan). Los
     * diales del taller son el valor POR DEFECTO con el que nace un estimado
     * —se copian a `pricingSnapshot` al crearlo—; a partir de ahí la política
     * pertenece a ESTE documento. Leyendo los ajustes vivos, cambiar el dial en
     * Ajustes ▸ Precios re-cotizaba en silencio la siguiente edición de un
     * presupuesto que ya estaba armado, y dos compañeros con el mismo estimado
     * abierto veían números distintos.
     *
     * LA CAÍDA A LOS AJUSTES es para los estimados viejos, guardados antes de
     * que el snapshot llevara estos campos: sin ella su política sería `NaN` y
     * toda fila se cotizaría a 0. */
    const pricingPolicy: LinePricingDials = {
        targetMarginPercent: Number.isFinite(snapshot.targetMarginPercent)
            ? snapshot.targetMarginPercent
            : settings.pricing.targetMarginPercent,
        materialMarkupPercent: Number.isFinite(snapshot.materialMarkupPercent)
            ? snapshot.materialMarkupPercent
            : settings.pricing.defaultMaterialMarkupPercent,
    };

    /**
     * Cambia la política de ESTE estimado. No toca `settings.pricing.*` —los
     * ajustes del taller se editan en Ajustes ▸ Precios y en ningún otro sitio—
     * y no mueve ni un precio: re-cotizar las partidas que ya están escritas es
     * una acción aparte y explícita (`applyMargin` / `applyMaterialMarkup`).
     *
     * Escribe SOLO los dos campos de política. El resto del snapshot —el costo
     * congelado, y también `netMarginTarget` / `netMarginFactor`— se conserva
     * con el `...prev`: perder el objetivo de margen neto al teclear un markup
     * dejaría las filas escaladas por un `k` que ya nadie recuerda.
     */
    const setPricingPolicy = (cambios: PricingPolicyPatch) => {
        if (!preciosEditables) return;
        setSnapshot(prev => {
            const siguiente: PricingSnapshotV2 = { ...prev };
            /* Los mismos topes que `utils/lineItemPricing` aplica al cotizar: un
             * margen del 100% pide precio infinito y un markup negativo vendería
             * por debajo del costo sin decirlo. Se recortan AQUÍ, al guardar, para
             * que lo que enseña la caja sea lo que de verdad se va a usar. */
            if (Number.isFinite(cambios.targetMarginPercent as number)) {
                siguiente.targetMarginPercent = Math.min(Math.max(cambios.targetMarginPercent as number, 0), 99);
            }
            if (Number.isFinite(cambios.materialMarkupPercent as number)) {
                siguiente.materialMarkupPercent = Math.max(cambios.materialMarkupPercent as number, 0);
            }
            return siguiente;
        });
    };

    /* El costo cargado por hora-hombre según el snapshot congelado.
     *
     * Había CUATRO copias de esta expresión en este archivo, y ninguna
     * guardaba contra un breakEvenRate de 0 —que es lo que devuelve
     * services/metrics.ts en un taller sin horas facturables—. Ahora todas
     * llaman a la de services/estimateMath, que sí guarda. */
    const laborCostPerHour = () => laborCostPerHourFromSnapshot(snapshot);

    /* ── El precio de una fila, en un solo sitio ─────────────────────────────
     *
     * Las tres funciones de abajo son TODA la aritmética de precio que queda en
     * este archivo. Cada rama de `updateItem`, `applyMargin` y
     * `applyMaterialMarkup` pasa por ellas, y por eso la invariante A.1 se
     * cumple por construcción y no por vigilancia. */

    /**
     * El costo con el que se cotiza una fila.
     *
     * Prefiere el que la fila declara —el que la pantalla pinta en la columna
     * COSTO— y solo para la mano de obra sin costo propio cae al snapshot. Es el
     * MISMO criterio que usa `laborCostOfItem` en el motor de totales: si la
     * fila y la cabecera costearan distinto, el margen de la cabecera dejaría de
     * poder explicarse con los números de la pantalla.
     */
    const costoEfectivo = (item: EstimateItem): number => {
        const declarado = Number.isFinite(item.cost) ? item.cost : 0;
        if (declarado > 0) return declarado;
        return item.type === 'Labor' ? laborCostPerHour() : declarado;
    };

    /**
     * El precio BASE de una fila y sus dos porcentajes: del pin si el
     * contratista fijó algo, y si no de la política del estimado.
     *
     * COSTO 0 NO SIGNIFICA «SE REGALA». La regla no puede producir un precio sin
     * costo del que partir (`priceLineByType` devuelve 0 a propósito, para no
     * inventar dinero), pero eso no autoriza a BORRAR un precio que ya existía:
     * `applyMargin` ponía `rate: 0` en toda fila de costo 0, y una movilización
     * de $500 a precio fijo se iba a $0,00 en cuanto el deslizador la rozaba.
     * Sin costo, la línea base que la fila ya tenía ES su línea base.
     *
     * El pase directo queda fuera de esa salvaguarda: un Fee vale su costo y
     * punto, aunque ese costo sea 0. Conservarle un precio viejo sería cobrarle
     * al cliente por encima de un recibo que puede leer.
     */
    const cotizarBase = (item: EstimateItem, policy: LinePricingDials = pricingPolicy) => {
        const precio = repriceFromPin({ ...item, cost: costoEfectivo(item) }, policy);
        const hayCosto = costoEfectivo(item) > 0;
        const baseRate = (hayCosto || isPassThroughType(item.type) || precio.rate > 0)
            ? precio.rate
            : baseRateOf(item);
        return { baseRate, markupPercent: precio.markupPercent, marginPercent: precio.marginPercent };
    };

    /**
     * La invariante, escrita una vez: `rate = baseRate × k`, `total = rate × qty`.
     * El pase directo no escala — de eso se encarga `applyFactor`, que conoce la
     * única definición de quién es pase directo.
     */
    const conInvariante = (item: EstimateItem, baseRate: number, k: number): EstimateItem => {
        const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
        const rate = applyFactor(baseRate, k, item.type);
        return { ...item, baseRate, rate, total: rate * quantity };
    };

    // --- ACTIONS ---

    /**
     * Mete una partida recién creada EN LA ESCALA DEL ESTIMADO.
     *
     * Todas las rutas de alta —Biblioteca, calculadoras, plantillas, modal de
     * material— cotizan con la política del estimado y estampan
     * `baseRate = rate`, o sea con k = 1. Eso es correcto: la REGLA de la fila
     * no depende del dial. Lo que faltaba era el segundo paso.
     *
     * SIN ESTO, una partida agregada DESPUÉS de mover el dial de margen neto
     * entraba sin escalar, y el resultado era medible: con k = 1,0870, un
     * material de $20 agregado antes del solve se cobraba a $26,0870 y uno
     * idéntico agregado después a $24,00 — **$208,70 de diferencia en una fila
     * de 100 unidades**, en el mismo documento, sin ningún número raro en
     * pantalla. El margen del trabajo propio se caía de 30,00% a 26,06% solo
     * por añadir la fila, y la interfaz lo presentaba como una re-medición
     * legítima en vez de como una fila fuera de escala.
     *
     * Y había una segunda mitad peor: en cuanto alguien tocaba esa fila —aunque
     * fuese solo la CANTIDAD—, `updateItem` sí pasaba por `conInvariante` y el
     * precio saltaba solo de $24,00 a $26,09. Editar una cantidad subía la
     * tarifa un 8,7%.
     *
     * Es la invariante A.6.4 («rate = baseRate × k, SIEMPRE») y la A.6.6 («una
     * fila añadida después de un solve se cotiza en la misma escala que una
     * idéntica añadida antes»). `baseRate` era necesario pero no suficiente:
     * conservarlo no sirve de nada si nadie multiplica.
     */
    const enEscalaDelEstimado = (item: EstimateItem): EstimateItem =>
        conInvariante(item, baseRateOf(item), netMarginFactor);

    const addItem = (item: EstimateItem) => {
        setItems(prev => [...prev, enEscalaDelEstimado(item)]);
    };

    const addZone = (zone: EstimateZone) => setZones(prev => [...prev, zone]);

    /** Drop a template's line items into the estimate, optionally creating a
     *  fresh zone for them. Default behavior (no targetZoneId) auto-creates a
     *  zone named after the template and stamps it with `sourceTemplateId` so
     *  the Save flow can detect template-derived zones and prompt "save as
     *  private template?". When the user explicitly picked an existing zone
     *  via the dropdown, we just append the items there — no new zone, no
     *  sourceTemplateId stamp (the existing zone may already mix sources). */
    const addTemplateItems = (
        templateItems: EstimateItem[],
        template: { id: string; name: string },
        targetZoneId?: string,
    ) => {
        // Mismo motivo que en `addItem`: una zona entera soltada después de
        // mover el dial entraba sin escalar, fila por fila.
        if (targetZoneId) {
            setItems(prev => [...prev, ...templateItems.map(i => enEscalaDelEstimado({ ...i, zoneId: targetZoneId }))]);
            return;
        }
        const newZoneId = Math.random().toString(36).substr(2, 9);
        const newZone: EstimateZone = {
            id: newZoneId,
            name: template.name,
            sourceTemplateId: template.id,
        };
        setZones(prev => [...prev, newZone]);
        setItems(prev => [...prev, ...templateItems.map(i => enEscalaDelEstimado({ ...i, zoneId: newZoneId }))]);
    };
    const updateZone = (id: string, updates: Partial<EstimateZone>) => {
        setZones(prev => prev.map(z => z.id === id ? { ...z, ...updates } : z));
    };
    const removeZone = (id: string) => {
        setZones(prev => prev.filter(z => z.id !== id));
        // Also clear zoneId from items
        setItems(prev => prev.map(i => i.zoneId === id ? { ...i, zoneId: undefined } : i));
    };

    /**
     * La única puerta por la que una partida cambia mientras se edita.
     *
     * Cinco ramas, una por cosa que el contratista puede tocar, y todas terminan
     * igual: se calcula el `baseRate` que corresponde y se pasa por
     * `conInvariante`. Ninguna deriva un precio por su cuenta.
     *
     * Las ramas de precio son EXCLUYENTES y en este orden: tipo → tarifa →
     * porcentaje → costo → cantidad. La interfaz manda un campo por gesto, así
     * que el orden solo decide qué gana en el caso raro de recibir dos; antes se
     * aplicaban en cascada y una actualización con costo Y tarifa se cotizaba
     * dos veces, quedando en la segunda.
     */
    /** ¿Este update trae algún campo que por sí solo re-cotiza la fila?
     *  Sirve para distinguir un despinado a secas de un despinado que llega
     *  acompañado (p. ej. al teclear una tarifa, que ya fija su propio pin). */
    const tocaElPrecio = (u: Partial<EstimateItem>) =>
        u.type !== undefined || u.rate !== undefined || u.cost !== undefined ||
        u.marginPercent !== undefined || u.markupPercent !== undefined;

    /** Los campos por los que una fila cambia de PRECIO. En un estimado cerrado
     *  se descartan; el resto del update (descripción, zona, sello de pricebook)
     *  sigue pasando, porque anotar de dónde salió una fila no le cambia lo que
     *  el cliente ya vio. */
    const CAMPOS_DE_PRECIO = [
        'type', 'rate', 'cost', 'marginPercent', 'markupPercent',
        'pricePinned', 'pinnedPercent', 'pinnedMetric',
    ] as const;

    const sinCamposDePrecio = (u: Partial<EstimateItem>): Partial<EstimateItem> => {
        const limpio: Partial<EstimateItem> = { ...u };
        for (const campo of CAMPOS_DE_PRECIO) delete limpio[campo];
        return limpio;
    };

    const updateItem = (id: string, entrada: Partial<EstimateItem>) => {
        const updates = preciosEditables ? entrada : sinCamposDePrecio(entrada);
        if (Object.keys(updates).length === 0) return;
        setItems(prev => prev.map(item => {
            if (item.id !== id) return item;

            const updated = { ...item, ...updates };
            const k = netMarginFactor;

            /* 1. Cambio de TIPO: se BORRA el pin y se re-cotiza con la regla del
             * tipo nuevo (Parte A.3).
             *
             * El porcentaje viejo estaba en otra escala y arrastrarlo cruzaría
             * dos unidades: un Equipment al 30% de MARGEN convertido en Material
             * enseñaba «43%» sin que se moviera un centavo del precio —la celda
             * empieza a leer `markupPercent` en vez de `marginPercent`— y ese
             * 42,857% se quedaba de markup permanente, listo para viajar al
             * pricebook al guardar.
             *
             * El precio cambia, que es lo honesto: un material de $20 vale
             * $24,00 con el dial al 20%, no $28,57. */
            if (updates.type !== undefined && updates.type !== item.type) {
                updated.pricePinned = undefined;
                updated.pinnedPercent = undefined;
                updated.pinnedMetric = undefined;
                const precio = cotizarBase(updated);
                updated.markupPercent = precio.markupPercent;
                updated.marginPercent = precio.marginPercent;
                return conInvariante(updated, precio.baseRate, k);
            }

            /* 1b. DESPINADO: «volver a la tarifa del taller».
             *
             * Es el botón que la tarjeta ofrece junto a la marca de «fijado a
             * mano» (components/estimate/EstimateLineItems.tsx), y manda solo
             * las tres banderas del pin, sin costo ni tarifa ni porcentaje.
             *
             * Sin esta rama el update no casaba con NINGUNA de las de abajo
             * —todas piden un campo de precio— y caía al `return updated` del
             * final: la bandera se borraba, la marca desaparecía de la
             * pantalla... y el precio se quedaba exactamente donde lo había
             * dejado el pin. El contratista pulsaba «volver a la tarifa del
             * taller» y no volvía nada; solo perdía la única señal de que esa
             * fila estaba fuera de la política.
             *
             * Despinar es, por definición, re-cotizar desde la política del
             * estimado: eso es lo que significa «volver al taller». */
            if (updates.pricePinned === false && !tocaElPrecio(updates)) {
                updated.pricePinned = undefined;
                updated.pinnedPercent = undefined;
                updated.pinnedMetric = undefined;
                const precio = cotizarBase(updated);
                updated.markupPercent = precio.markupPercent;
                updated.marginPercent = precio.marginPercent;
                return conInvariante(updated, precio.baseRate, k);
            }

            /* 2. Cambio de TARIFA: el contratista teclea el precio entero.
             *
             * `baseRate = tecleada / k`, NO `baseRate = tecleada`. Las dos
             * opciones dejan la fila en el precio que se acaba de escribir; la
             * diferencia está en lo que pasa después. Dividiendo, la fila queda
             * en la misma escala que todas las demás y el dial la sigue moviendo
             * con el resto, que es lo que promete el modelo: el pin fija el
             * precio RELATIVO, el dial el NIVEL absoluto (A.3). Guardando la
             * tecleada tal cual, la fila quedaría fuera de la invariante
             * `rate = baseRate × k` —la única regla que este archivo existe para
             * sostener— y sería la primera excepción de una lista que crece.
             *
             * El pase directo no escala, así que ahí dividir y no dividir es lo
             * mismo: `applyFactor` lo devuelve intacto. */
            if (updates.rate !== undefined) {
                const tecleada = Number.isFinite(updates.rate) ? Math.max(updates.rate, 0) : 0;
                if (isPassThroughType(updated.type)) {
                    // Un Fee va a costo: no admite ni tarifa tecleada ni pin.
                    const precio = cotizarBase({ ...updated, pricePinned: undefined });
                    updated.pricePinned = undefined;
                    updated.pinnedPercent = undefined;
                    updated.pinnedMetric = undefined;
                    updated.markupPercent = precio.markupPercent;
                    updated.marginPercent = precio.marginPercent;
                    return conInvariante(updated, precio.baseRate, k);
                }
                const factor = Number.isFinite(k) && k > 0 ? k : 1;
                const baseRate = tecleada / factor;
                updated.pricePinned = true;
                /* En un pin de tarifa, `pinnedPercent` guarda el precio BASE, no
                 * un porcentaje: es lo que `repriceFromPin` lee para reconstruir
                 * la fila cuando cambie el costo. El nombre del campo miente un
                 * poco; el metro dice la verdad. */
                updated.pinnedPercent = baseRate;
                updated.pinnedMetric = 'rate';
                /* Los porcentajes describen la fila EN SU BASE, igual que en
                 * cualquier otra fila: `scaleItemsByFactor` no los reescribe al
                 * escalar, y la tarjeta enseña «X% · ×k». Derivarlos del precio
                 * ya escalado fabricaría el porcentaje compuesto que nadie
                 * tecleó (A.5). */
                const precio = cotizarBase(updated);
                updated.markupPercent = precio.markupPercent;
                updated.marginPercent = precio.marginPercent;
                return conInvariante(updated, precio.baseRate, k);
            }

            /* 3. Cambio de PORCENTAJE: se fija el pin y se re-cotiza.
             *
             * EL METRO LO DECIDE EL TIPO, no el campo por el que llegó el
             * número: Material va por MARKUP (sobre el costo) y todo lo demás
             * por MARGEN (sobre el precio). Aquí estaba el fallo reportado —esta
             * rama usaba `costo / (1 - margen)` TAMBIÉN para materiales, así que
             * quien tecleaba 30 en un material de $20 recibía $28,57 y una
             * insignia que decía 42,9%. Ahora recibe $26,00 y la insignia dice
             * 30,0% de markup, que es lo que escribió. */
            if (updates.marginPercent !== undefined || updates.markupPercent !== undefined) {
                if (isPassThroughType(updated.type)) {
                    /* Un Fee no admite porcentaje: se remite a costo. El número
                     * que venga en `updates` se descarta aquí en vez de quedarse
                     * guardado esperando a que otra rama lo aplique. */
                    updated.pricePinned = undefined;
                    updated.pinnedPercent = undefined;
                    updated.pinnedMetric = undefined;
                    const precio = cotizarBase(updated);
                    updated.markupPercent = precio.markupPercent;
                    updated.marginPercent = precio.marginPercent;
                    return conInvariante(updated, precio.baseRate, k);
                }
                const metro: 'markup' | 'margin' = updated.type === 'Material' ? 'markup' : 'margin';
                const tecleado = metro === 'markup'
                    ? (updates.markupPercent ?? updates.marginPercent)
                    : (updates.marginPercent ?? updates.markupPercent);
                updated.pricePinned = true;
                updated.pinnedPercent = Number.isFinite(tecleado as number) ? (tecleado as number) : 0;
                updated.pinnedMetric = metro;
                /* La mano de obra sin costo propio se apoya en el del snapshot, y
                 * se DEJA ESCRITO: si no, la fila enseñaría un costo de $0 al
                 * lado de un precio calculado sobre $51,77. */
                if (updated.type === 'Labor' && !(updated.cost > 0)) updated.cost = laborCostPerHour();
                const precio = cotizarBase(updated);
                updated.markupPercent = precio.markupPercent;
                updated.marginPercent = precio.marginPercent;
                return conInvariante(updated, precio.baseRate, k);
            }

            /* 4. Cambio de COSTO: se re-cotiza desde el pin si lo hay, y desde la
             * política si no (A.3).
             *
             * Un material fijado al 30% de markup mantiene su 30% y mueve el
             * precio, que es justo lo que el contratista espera al corregir una
             * factura del proveedor. Antes esta rama tenía su propia fórmula por
             * tipo y la mano de obra ni siquiera entraba. */
            if (updates.cost !== undefined) {
                const precio = cotizarBase(updated);
                updated.markupPercent = precio.markupPercent;
                updated.marginPercent = precio.marginPercent;
                return conInvariante(updated, precio.baseRate, k);
            }

            // 5. Cambio de CANTIDAD: solo el total. Los porcentajes son razones
            //    POR UNIDAD — cobrar 100 unidades no cambia el margen de una.
            if (updates.quantity !== undefined) {
                if (item.calcBasis) updated.isOverridden = true;
                /* Con `baseRate` se re-afirma la invariante; sin él (fila
                 * anterior a este modelo) se respeta el `rate` que hay, porque
                 * ahí «lo que se ve hoy» ES la línea base y multiplicarlo por k
                 * cobraría el factor dos veces. */
                if (Number.isFinite(updated.baseRate as number)) {
                    return conInvariante(updated, updated.baseRate as number, k);
                }
                const quantity = Number.isFinite(updated.quantity) ? updated.quantity : 0;
                updated.total = quantity * updated.rate;
            }

            return updated;
        }));
    };

    const removeItem = (id: string) => {
        setItems(prev => prev.filter(i => i.id !== id));
    };

    /** El estimado tal como lo consume el motor de totales. Una sola forma de
     *  armarlo, para que la cabecera y el solver midan exactamente lo mismo. */
    const entradaDeTotales = (paraItems: EstimateItem[]): EstimateMathInput => ({
        items: paraItems,
        adjustments,
        siteConditions,
        pricingSnapshot: snapshot,
        rules: {
            tripCharge: settings.rules.tripCharge,
            emergencySurchargePercent: settings.rules.emergencySurchargePercent,
            minServiceCall: settings.rules.minServiceCall,
        },
    });

    /**
     * El dial de margen neto (Parte A.4): pide un margen SOBRE EL TRABAJO PROPIO
     * y el solver devuelve el factor `k` que lo consigue.
     *
     * Si el objetivo es alcanzable se guarda `k` y se re-escalan las partidas
     * desde su `baseRate` —nunca desde el precio ya escalado, que es lo que hacía
     * el deslizador viejo y lo que volvía el resultado dependiente del camino—.
     * Si NO lo es, no se toca ni un precio: se devuelve el resultado con su
     * motivo y se deja accesible en `netMarginSolve`. Un dial que se mueve sin
     * cambiar nada es preferible a un número inventado, pero un dial que cambia
     * los precios a algo que no es lo pedido no lo es.
     *
     * `null` devuelve el estimado a k = 1, el ancla honesta: exactamente los
     * diales del taller.
     */
    const setNetMarginTarget = (percent: number | null): MarginSolveResult | null => {
        /* Un estimado cerrado no mueve precios ni por el deslizador. La interfaz
         * ya lo deja en solo lectura; esta guarda es la que impide que un camino
         * nuevo —o una prueba— se salte la pantalla y reescriba un documento que
         * el cliente ya firmó. */
        if (!preciosEditables) return null;
        if (percent === null) {
            setItems(prev => scaleItemsByFactor(prev, 1));
            setNetMarginFactor(1);
            setNetMarginTargetState(null);
            setNetMarginSolve(null);
            setSnapshot(prev => ({ ...prev, netMarginTarget: null, netMarginFactor: 1 }));
            return null;
        }
        const resultado = solveScaleForNetMargin(entradaDeTotales(items), percent);
        setNetMarginSolve(resultado);
        if (!resultado.feasible) return resultado;
        setItems(prev => scaleItemsByFactor(prev, resultado.k));
        setNetMarginFactor(resultado.k);
        setNetMarginTargetState(percent);
        setSnapshot(prev => ({ ...prev, netMarginTarget: percent, netMarginFactor: resultado.k }));
        return resultado;
    };

    /**
     * Aplica la POLÍTICA a las partidas que ya están en el presupuesto.
     *
     * YA NO ES EL MECANISMO DE NINGÚN DESLIZADOR ni de ningún guardado — el
     * dial es `setNetMarginTarget` y el solver, y cambiar la política es
     * `setPricingPolicy`, que no mueve un solo precio. Esta función sobrevive
     * como la acción explícita «aplica la política también a las 12 líneas que
     * ya están escritas», detrás del botón del panel de Precio rápido. Nunca
     * corre sola: re-cotizar en silencio un documento que el contratista ya
     * repasó es cómo se pierde la confianza en el número de abajo.
     *
     * Se fue de aquí la regla ADITIVA `markupMaterial = markupBase + (margen
     * objetivo − margen base)`. Sumaba puntos de MARGEN a puntos de MARKUP —dos
     * unidades distintas— y era la causa medida de que el deslizador no fuera
     * monótono (posición 11 → 6,26% de margen; posición 10 → 13,46%) ni estable
     * (clic al 10 → 13,46%; arrastre al 10 → 5,71%). El margen del estimado no
     * se persigue sumando puntos a mano: se resuelve.
     *
     * Tres reglas que esta función respeta y la anterior no:
     *   · una fila FIJADA no se toca (su porcentaje es una decisión del
     *     contratista, no un valor por defecto que se pueda pisar);
     *   · el pase directo se re-cotiza a costo, nunca con margen;
     *   · una fila sin costo conserva su precio en vez de irse a $0,00.
     */
    const applyMargin = (targetMargin: number, opts?: { scaleMaterials?: boolean }) => {
        if (!preciosEditables) return;
        /* `scaleMaterials` decide si los materiales se refrescan también a SU
         * dial (el de markup). Nunca reciben el número del margen: 30 puntos de
         * margen no son 30 puntos de markup. */
        const scaleMaterials = opts?.scaleMaterials ?? true;
        setTargetMarginGoal(targetMargin);
        const safeMargin = Math.min(Math.max(targetMargin, 0), 99);
        const policy: LinePricingDials = { ...pricingPolicy, targetMarginPercent: safeMargin };
        const k = netMarginFactor;

        setItems(prevItems => prevItems.map(item => {
            if (item.type === 'Material' && !scaleMaterials) return item;
            /* Una sola llamada para las dos clases de fila: `cotizarBase`
             * respeta el pin si lo hay y aplica la política si no. La fila
             * fijada sale con el mismo porcentaje que tenía —su pin lo produce—
             * y con la invariante re-afirmada. */
            const precio = cotizarBase(item, policy);
            return conInvariante(
                { ...item, markupPercent: precio.markupPercent, marginPercent: precio.marginPercent },
                precio.baseRate,
                k,
            );
        }));
    };

    /** Re-cotiza las filas de Material al markup indicado —SOBRE EL COSTO—.
     *  Misma pareja que `applyMargin`: acción explícita, no mecanismo del dial.
     *  Respeta el pin, mantiene la invariante y no hunde a $0 una fila sin costo. */
    const applyMaterialMarkup = (markup: number) => {
        if (!preciosEditables) return;
        const safeMarkup = Math.max(markup, 0);
        const policy: LinePricingDials = { ...pricingPolicy, materialMarkupPercent: safeMarkup };
        const k = netMarginFactor;
        setItems(prev => prev.map(item => {
            if (item.type !== 'Material') return item;
            const precio = cotizarBase(item, policy);
            return conInvariante(
                { ...item, markupPercent: precio.markupPercent, marginPercent: precio.marginPercent },
                precio.baseRate,
                k,
            );
        }));
    };

    // --- CALCULATIONS ---
    // All money math (subtotal, tax, grand total, cost basis, margin) lives in
    // services/estimateMath.ts. The builder, the estimates list, and the detail
    // drawer all call computeEstimateTotals() so they can never drift apart.
    const totals = computeEstimateTotals(entradaDeTotales(items));

    /* ¿Hay trabajo sin guardar?
     *
     * Se compara contra una huella tomada en el PRIMER render, no contra
     * `initialEstimate` directamente: varios campos (proposalSettings,
     * siteConditions) reciben valores por defecto cuando el presupuesto no los
     * trae, así que comparar con el objeto crudo daría "sucio" desde el
     * instante de abrir, y la pregunta de confirmación saldría siempre —
     * que es la forma más rápida de que la gente aprenda a ignorarla.
     *
     * Lo vigilado es lo que se pierde al cerrar: partidas, zonas, ajustes,
     * condiciones de obra y los textos de la propuesta. El `snapshot` queda
     * fuera a propósito: se recaptura solo desde los ajustes y no es trabajo
     * que el usuario haya tecleado. */
    const huella = useMemo(
        () => JSON.stringify({ items, zones, adjustments, proposalSettings, siteConditions }),
        [items, zones, adjustments, proposalSettings, siteConditions],
    );
    const huellaInicial = useRef<string | null>(null);
    if (huellaInicial.current === null) huellaInicial.current = huella;
    const hayCambiosSinGuardar = huella !== huellaInicial.current;

    return {
        // State
        items,
        adjustments,
        proposalSettings,
        snapshot,
        targetMarginGoal,
        hayCambiosSinGuardar,
        /* La política de precios DE ESTE ESTIMADO (decisión 2), y si todavía se
         * puede tocar. La pantalla las enseña; no las calcula. */
        pricingPolicy,
        preciosEditables,
        // El dial de margen neto (Parte A.4)
        netMarginFactor,
        netMarginTarget,
        netMarginSolve,
        // Computed — full canonical totals from services/estimateMath.ts
        totals,
        // Setters
        setAdjustments,
        setProposalSettings,
        setNetMarginTarget,
        setPricingPolicy,
        // Actions
        addItem,
        updateItem,
        removeItem,
        applyMargin,
        applyMaterialMarkup,
        refreshSnapshotFromCurrentSettings,
        // New Hooks
        siteConditions,
        setSiteConditions,
        zones,
        addZone,
        addTemplateItems,
        updateZone,
        removeZone
    };
};
